import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Badge, Button, FileUpload, Modal, Select, Table } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { useStaffingPeopleContext } from "./StaffingPeopleContext";
import { errorMessages } from "./staffingFormatters";
import { SAMPLE_CSV, SAMPLE_FILE_NAME } from "./sampleRosterCsv";

const STEP_LABELS = ["Upload a file", "Match columns", "Preview and import"];
const SKIP = "skip";

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

const skippedNote = (count) => (count ? ` ${count} ${count === 1 ? "row" : "rows"} skipped.` : "");

const formatRate = (rate) => (!rate ? "—" : /^\$?\d/.test(rate) ? `$${rate.replace(/^\$/, "")}` : rate);

const PeopleImportModal = ({ onClose, onImported }) => {
  const { paths, importFields, importMaxBytes } = useStaffingPeopleContext();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [csv, setCsv] = useState(null);
  const [isSample, setIsSample] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);

  const maxMegabytes = importMaxBytes / 1024 / 1024;

  const loadPreview = async (csvText, nextMapping) => {
    setBusy(true);
    setErrors([]);
    try {
      const { data } = await client.post(paths.importPreview, { staff_member_import: { csv: csvText, mapping: nextMapping } });
      setPreview(data);
      setMapping(data.mapping);
      return data;
    } catch (error) {
      setErrors(errorMessages(error));
      toastError("Could not read this file.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openFile = async (name, size, text, sample) => {
    setFile({ id: name, name, size, progress: 100 });
    setCsv(text);
    setIsSample(sample);
    setPreview(null);
    const data = await loadPreview(text, {});
    if (data?.readable) setStep(2);
  };

  const selectFiles = async ([selected]) => {
    if (!selected) return;
    if (selected.size > importMaxBytes) {
      setFile({ id: selected.name, name: selected.name, size: selected.size, error: `The file is too large. Upload a file under ${maxMegabytes} MB.` });
      setPreview(null);
      return;
    }
    setBusy(true);
    try {
      const text = await readFile(selected);
      await openFile(selected.name, selected.size, text, false);
    } catch (error) {
      setPreview(null);
      setErrors([error.message]);
      toastError("Could not read this file.");
    } finally {
      setBusy(false);
    }
  };

  const loadSample = () => openFile(SAMPLE_FILE_NAME, SAMPLE_CSV.length, SAMPLE_CSV, true);

  const fieldForHeader = (header) => Object.keys(mapping).find((field) => mapping[field] === header) || SKIP;

  const changeColumn = (header, field) => {
    const nextMapping = Object.fromEntries(Object.entries(mapping).filter(([key, value]) => value !== header && key !== field));
    if (field !== SKIP) nextMapping[field] = header;
    setMapping(nextMapping);
  };

  const goBack = () => {
    setErrors([]);
    setStep(step - 1);
  };

  const previewRows = async () => {
    if (await loadPreview(csv, mapping)) setStep(3);
  };

  const runImport = async () => {
    setBusy(true);
    setErrors([]);
    try {
      const { data } = await client.post(paths.import, { staff_member_import: { csv, mapping } });
      toastSuccess(`Imported ${data.imported} staff.${skippedNote(data.skipped.length)} Invite them when you're ready.`);
      onImported();
      onClose();
    } catch (error) {
      setErrors(errorMessages(error));
      toastError("Could not import this file.");
    } finally {
      setBusy(false);
    }
  };

  const fieldOptions = [{ value: SKIP, label: "Skip" }, ...importFields.map((field) => ({ value: field.key, label: field.label }))];
  const fileErrors = preview?.fileErrors || [];
  const showRate = importFields.some((field) => field.key === "hourly_rate");
  const canImport = step === 3 && !isSample && fileErrors.length === 0 && preview.validCount > 0 && !busy;

  const mappingColumns = [
    { key: "column", header: "Column in file", render: ({ header }) => <strong>{header}</strong> },
    { key: "sample", header: "First row", render: ({ header }) => preview.firstRow[header] || "—" },
    {
      key: "field",
      header: "Opendate field",
      width: 220,
      render: ({ header, index }) => (
        <Select
          id={`staff-import-column-${index}`}
          size="sm"
          aria-label={`Opendate field for ${header}`}
          disabled={busy}
          options={fieldOptions}
          value={fieldForHeader(header)}
          onChange={(field) => changeColumn(header, field || SKIP)}
        />
      ),
    },
  ];

  const rowColumns = [
    {
      key: "name",
      header: "Name",
      render: (row) => (
        <>
          <div>{[row.values.first_name, row.values.last_name].filter(Boolean).join(" ") || "—"}</div>
          {row.errors.length > 0 && (
            <div className="staff-import-row-errors" style={{ color: "var(--sl-text-error)" }}>
              {row.errors.join("; ")}
            </div>
          )}
        </>
      ),
    },
    { key: "phone", header: "Phone", render: (row) => row.values.phone || "—" },
    { key: "email", header: "Email", render: (row) => row.values.email || "—" },
    { key: "positions", header: "Position", render: (row) => row.values.positions || "—" },
    ...(showRate ? [{ key: "rate", header: "Rate", width: 80, render: (row) => formatRate(row.values.hourly_rate) }] : []),
  ];

  return (
    <Modal
      open
      size="lg"
      width={880}
      title="Import staff from CSV"
      description={`${STEP_LABELS[step - 1]} · step ${step} of 3`}
      onClose={onClose}
      footer={
        <div className="d-flex justify-content-end w-100" style={{ gap: 8 }}>
          {step === 1 ? (
            <Button variant="secondary" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
          ) : (
            <Button variant="secondary" disabled={busy} onClick={goBack}>
              Back
            </Button>
          )}
          {step === 1 && preview?.readable && (
            <Button variant="primary" disabled={busy} onClick={() => setStep(2)}>
              Match columns
            </Button>
          )}
          {step === 2 && (
            <Button variant="primary" disabled={busy || !mapping.first_name} onClick={previewRows}>
              Preview rows
            </Button>
          )}
          {step === 3 && (
            <Button variant="primary" disabled={!canImport} onClick={runImport}>
              {`Import ${preview.validCount} staff`}
            </Button>
          )}
        </div>
      }
    >
      {errors.length > 0 && (
        <div className="mb-3">
          <Alert color="error" title={step === 3 ? "Could not import" : "Could not read this file"} description={errors.join(" ")} />
        </div>
      )}

      {step === 1 && (
        <>
          <FileUpload
            accept=".csv,text/csv"
            hint={`CSV up to ${maxMegabytes} MB. Homebase "Team" exports work as is.`}
            files={file ? [{ ...file, error: file.error || (preview && !preview.readable ? fileErrors.join(" ") : undefined) }] : []}
            onFilesSelected={selectFiles}
            disabled={busy}
          />
          <div className="d-flex align-items-center mt-3 p-3" style={{ gap: 12, borderRadius: 8, background: "var(--sl-bg-secondary)" }}>
            <span className="text-muted" style={{ flex: 1 }}>
              Moving off Homebase? Export Team → Download CSV, then drop it here. Positions and wage rates come across.
            </span>
            <Button variant="secondary" size="sm" disabled={busy} onClick={loadSample}>
              Use sample file
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
            <Badge color="gray" size="sm">
              {file.name}
            </Badge>
            <span className="text-muted">
              {preview.rowCount} {preview.rowCount === 1 ? "row" : "rows"} · we matched columns by name. Check them.
            </span>
          </div>
          <Table columns={mappingColumns} data={preview.headers.map((header, index) => ({ header, index }))} getRowKey={(row) => row.index} />
          {!mapping.first_name && <p className="text-muted mt-2 mb-0">Choose the column that holds first names.</p>}
        </>
      )}

      {step === 3 && (
        <>
          {fileErrors.length > 0 && (
            <div className="mb-3">
              <Alert color="error" title="This file can't be imported yet" description={fileErrors.join(" ")} />
            </div>
          )}
          {isSample && (
            <div className="mb-3">
              <Alert color="brand" title="This is a sample file. Upload your own to import." />
            </div>
          )}
          <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
            <Badge color="success" size="sm">
              {preview.validCount} ready
            </Badge>
            <Badge color="error" size="sm">
              {preview.invalidCount} skipped
            </Badge>
          </div>
          {preview.newPositionNames.length > 0 && (
            <p className="text-muted mb-2">New positions will be added to General: {preview.newPositionNames.join(", ")}.</p>
          )}
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            <Table columns={rowColumns} data={preview.rows} getRowKey={(row) => row.number} loading={busy} empty="No rows found in this file." />
          </div>
        </>
      )}
    </Modal>
  );
};

PeopleImportModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onImported: PropTypes.func.isRequired,
};

export default PeopleImportModal;
